import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Info, X } from 'lucide-react';
import { OMS_COLORS, tint } from '../omsDashTheme';
import { BAND_META, type HealthBand, type HealthScore } from '../utils/healthScore';

/* ============================================================
 * Health-score visuals: the engraved dial, band chip and delta
 * chip. One palette entry per band — used identically everywhere.
 * ============================================================ */

export const BAND_COLOR: Record<HealthBand, { ink: string; fill: string }> = {
  excellent: { ink: '#1F5A47', fill: '#339274' },
  good: { ink: '#0B3B2E', fill: '#7BAF9B' },
  watch: { ink: '#8A5A08', fill: '#C9A961' },
  attention: { ink: '#9F1239', fill: '#9F1239' },
};

const NO_DATA = { ink: OMS_COLORS.slate, fill: OMS_COLORS.slate };

export function bandColor(score: HealthScore | null) {
  return score ? BAND_COLOR[score.band] : NO_DATA;
}

export function BandChip({ score }: { score: HealthScore | null }) {
  const palette = bandColor(score);
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em]"
      style={{ color: palette.ink, backgroundColor: tint(palette.fill, 0.16) }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: palette.fill }} />
      {score ? BAND_META[score.band].label : 'No scheduled work'}
    </span>
  );
}

/** "▲ 6 vs last week" — always shows its comparison window. */
export function DeltaChip({ delta, phrase }: { delta: number | null; phrase: string }) {
  if (delta === null) {
    return (
      <span className="text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
        no prior data
      </span>
    );
  }
  const up = delta > 0;
  const flat = delta === 0;
  const color = flat ? OMS_COLORS.slate : up ? '#1F5A47' : '#9F1239';
  return (
    <span className="text-[10px] font-extrabold tabular-nums" style={{ color }}>
      {flat ? '—' : up ? '▲' : '▼'} {flat ? 'level' : Math.abs(delta)} {phrase}
    </span>
  );
}

interface DialProps {
  score: HealthScore | null;
  size?: number;
  /** Accessible name, e.g. "Health score for Tamil Nadu". */
  label: string;
}

/**
 * Engraved score dial: hairline track ring, band-coloured sweep that draws
 * to the value, tabular numeral in the centre. Sweep collapses to a static
 * arc under prefers-reduced-motion.
 */
export function ScoreDial({ score, size = 64, label }: DialProps) {
  const reduced = useReducedMotion();
  const stroke = size >= 64 ? 5 : 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const value = score?.score ?? 0;
  const palette = bandColor(score);
  const target = c * (1 - value / 100);

  return (
    <div
      role="img"
      aria-label={`${label}: ${score ? `${score.score} of 100, ${BAND_META[score.band].label}` : 'no scheduled work in this window'}`}
      className="relative shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(11,59,46,0.10)"
          strokeWidth={stroke}
        />
        {score && (
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={palette.fill}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: reduced ? target : c }}
            animate={{ strokeDashoffset: target }}
            transition={{ duration: reduced ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span
          className="font-extrabold tabular-nums"
          style={{ color: score ? palette.ink : OMS_COLORS.caption, fontSize: size * 0.28 }}
        >
          {score ? score.score : '·'}
        </span>
        {score && (
          <span
            className="font-bold tabular-nums"
            style={{ color: OMS_COLORS.caption, fontSize: Math.max(size * 0.14, 6) }}
          >
            /100
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * "How this score is computed" — each component with its effective weight
 * and honest denominator, plus the on-time TODO note. Click-toggled so it
 * works on touch as well as hover.
 */
export function ScoreExplainer({ score }: { score: HealthScore }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="How this score is computed"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-5 w-5 items-center justify-center rounded-full transition hover:bg-[rgba(11,59,46,0.08)]"
        style={{ color: OMS_COLORS.caption }}
      >
        <Info className="h-3 w-3" />
      </button>
      {open && (
        <span
          role="note"
          className="absolute right-0 top-6 z-30 w-64 rounded-xl border border-[rgba(11,59,46,0.28)] p-3 text-left shadow-[0_18px_44px_-20px_rgba(42,31,23,0.40)]"
          style={{ backgroundColor: 'rgba(250,247,238,0.98)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="flex items-start justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.tealDark }}>
              How this score is computed
            </span>
            <button
              type="button"
              aria-label="Close"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              className="rounded p-0.5 hover:bg-[rgba(11,59,46,0.08)]"
              style={{ color: OMS_COLORS.caption }}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
          <span className="mt-2 block space-y-1.5">
            {score.components.map((comp) => (
              <span key={comp.key} className="block text-[11px] leading-snug" style={{ color: OMS_COLORS.ink }}>
                <span className="font-bold">{comp.label}</span>
                <span style={{ color: OMS_COLORS.caption }}> · {Math.round(comp.weight * 100)}% weight</span>
                <br />
                <span className="tabular-nums font-semibold">{comp.score}</span>
                <span style={{ color: OMS_COLORS.caption }}> — {comp.detail}</span>
              </span>
            ))}
          </span>
          <span className="mt-2 block border-t border-[rgba(11,59,46,0.14)] pt-1.5 text-[10px] leading-snug" style={{ color: OMS_COLORS.caption }}>
            On-time rate joins the model when per-task due-vs-decided dates are available from the API.
          </span>
        </span>
      )}
    </span>
  );
}
